import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine,
} from 'recharts';
import {
  ArrowLeft, Video, Link2, Trash2, Loader2, Upload, Play, Pause, Crosshair, MapPin,
  LineChart as LineChartIcon, EyeOff, Settings2, Plus, RotateCcw,
} from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Label } from './ui/label';
import { Input } from './ui/input';
import OverlayWidget from './OverlayWidget';
import AnnotationsPanel, { ANNOTATION_ICONS } from './AnnotationsPanel';
import { ReplayStatusBadge, ReplayMissingPlaceholder, BetaStorageDisclaimer } from './ReplayStatusUI';
import { API, BACKEND_URL } from '../config/api';

/**
 * ReplayStudio — onboard video synchronised with the telemetry already
 * linked to this tanda. MVP scope: see /app/backend/replay.py header.
 */

const ALL_CHANNELS = [
  { key: 'speed_kmh',      label: 'Velocidad', unit: 'km/h',  color: '#60a5fa', kpi: true,  overlayChart: true  },
  { key: 'rpm',            label: 'RPM',       unit: 'rpm',   color: '#a78bfa', kpi: true,  overlayChart: true  },
  { key: 'lat_g',          label: 'G lateral', unit: 'g',     color: '#fbbf24', kpi: false, overlayChart: true  },
  { key: 'long_g',         label: 'G long.',   unit: 'g',     color: '#4ade80', kpi: false, overlayChart: true  },
  { key: 'yaw_rate_dps',   label: 'Yaw Rate',  unit: 'deg/s', color: '#fb7185', kpi: true,  overlayChart: true  },
  { key: 'water_temp_c',   label: 'Agua',      unit: '°C',    color: '#22d3ee', kpi: false, overlayChart: false },
  { key: 'exhaust_temp_c', label: 'Escape',    unit: '°C',    color: '#f87171', kpi: false, overlayChart: false },
];

// Mini sparkline (fluid, fills its parent via SVG viewBox). The full lap is
// precomputed ONCE; per-frame work is done DIRECTLY on the DOM via refs so
// React never re-renders during playback — only the cursor x and value text
// change every frame, mutated imperatively. This is what keeps overlay sync
// rock-solid even on weaker laptops.
const OverlaySparkline = React.memo(function OverlaySparkline({
  samples, channel, color, getTime, subscribe, windowSec = 10,
  ghostSamples = null, ghostColor = '#ffffff',
}) {
  const X_SCALE = 10;
  const VH = 50, padY = 3;
  const svgRef = React.useRef(null);
  const cursorRef = React.useRef(null);
  const labelRef = React.useRef(null);
  const ghostLabelRef = React.useRef(null);

  const precomp = React.useMemo(() => {
    const arr = samples?.[channel.key];
    const t = samples?.time_s;
    if (!Array.isArray(arr) || !Array.isArray(t) || arr.length < 2) return null;
    let yMin = Infinity, yMax = -Infinity;
    for (let i = 0; i < arr.length; i++) {
      const v = arr[i];
      if (v == null || !Number.isFinite(v)) continue;
      if (v < yMin) yMin = v;
      if (v > yMax) yMax = v;
    }
    // Include ghost in y range so both lines fit. Skip if no ghost.
    const gArr = ghostSamples?.[channel.key];
    if (Array.isArray(gArr)) {
      for (let i = 0; i < gArr.length; i++) {
        const v = gArr[i];
        if (v == null || !Number.isFinite(v)) continue;
        if (v < yMin) yMin = v;
        if (v > yMax) yMax = v;
      }
    }
    if (!Number.isFinite(yMin) || !Number.isFinite(yMax) || yMax === yMin) {
      yMax = yMin + 1;
    }
    const step = Math.max(1, Math.floor(t.length / 900));
    let d = '';
    for (let i = 0; i < t.length; i += step) {
      const x = (t[i] * X_SCALE).toFixed(2);
      const y = (VH - padY - ((arr[i] - yMin) / (yMax - yMin)) * (VH - 2 * padY)).toFixed(2);
      d += (i === 0 ? 'M' : 'L') + x + ',' + y + ' ';
    }
    // Ghost path: project ghost values onto MAIN's time_s axis by going
    // through distance (so circuit positions line up, not stopwatch time).
    let dGhost = '';
    const mainDist = samples?.distance_m;
    const ghostDist = ghostSamples?.distance_m;
    if (Array.isArray(gArr) && Array.isArray(mainDist) && Array.isArray(ghostDist) && gArr.length > 1) {
      for (let i = 0; i < t.length; i += step) {
        const dHere = mainDist[i];
        const v = valueAtDistance(ghostSamples, channel.key, dHere);
        if (v == null || !Number.isFinite(v)) continue;
        const x = (t[i] * X_SCALE).toFixed(2);
        const y = (VH - padY - ((v - yMin) / (yMax - yMin)) * (VH - 2 * padY)).toFixed(2);
        dGhost += (dGhost ? 'L' : 'M') + x + ',' + y + ' ';
      }
    }
    return { d, dGhost, yMin, yMax, t, arr };
  }, [samples, ghostSamples, channel.key]);

  // Subscribe to the parent ticker. The callback mutates DOM directly so the
  // React component does NOT re-render on every animation frame.
  React.useEffect(() => {
    if (!precomp || !subscribe) return;
    const { t, arr } = precomp;
    const mainDist = samples?.distance_m;
    const decimals = channel.key === 'rpm' ? 0 : 2;
    const update = (tNow) => {
      const vbX = (tNow - windowSec * 0.7) * X_SCALE;
      const vbW = windowSec * X_SCALE;
      const cursorX = tNow * X_SCALE;
      if (svgRef.current) {
        svgRef.current.setAttribute('viewBox', `${vbX.toFixed(2)} 0 ${vbW.toFixed(2)} ${VH}`);
      }
      if (cursorRef.current) {
        cursorRef.current.setAttribute('x1', cursorX);
        cursorRef.current.setAttribute('x2', cursorX);
      }
      if (labelRef.current) {
        let v = null;
        if (tNow <= t[0]) v = arr[0];
        else if (tNow >= t[t.length - 1]) v = arr[t.length - 1];
        else {
          let lo = 0, hi = t.length - 1;
          while (lo < hi - 1) { const m = (lo + hi) >> 1; if (t[m] <= tNow) lo = m; else hi = m; }
          const f = (tNow - t[lo]) / Math.max(t[lo + 1] - t[lo], 1e-9);
          v = arr[lo] * (1 - f) + arr[lo + 1] * f;
        }
        labelRef.current.textContent = Number.isFinite(v) ? v.toFixed(decimals) : '—';
      }
      if (ghostLabelRef.current && ghostSamples && Array.isArray(mainDist)) {
        const dHere = valueAt(samples, 'distance_m', tNow);
        const gv = (dHere != null) ? valueAtDistance(ghostSamples, channel.key, dHere) : null;
        ghostLabelRef.current.textContent = Number.isFinite(gv) ? gv.toFixed(decimals) : '—';
      }
    };
    // Prime with the current time so the chart isn't empty before the first tick.
    if (typeof getTime === 'function') update(getTime());
    return subscribe(update);
  }, [precomp, subscribe, getTime, channel.key, windowSec, ghostSamples, samples]);

  if (!precomp) return null;
  const stroke = color || channel.color;
  // Initial viewBox uses tNow=0; the subscription will move it on first tick.
  return (
    <div
      className="w-full h-full bg-black/65 backdrop-blur rounded border border-white/10 px-1.5 pt-0.5 pb-1 select-none flex flex-col"
      data-testid={`overlay-chart-${channel.key}`}
    >
      <div className="flex items-center justify-between gap-2 text-[10px] leading-none mb-0.5">
        <span className="uppercase tracking-wide text-white/70 truncate">{channel.label}</span>
        <span className="font-mono whitespace-nowrap flex items-center gap-1.5">
          {ghostSamples && (
            <span style={{ color: ghostColor, opacity: 0.85 }} data-testid={`overlay-chart-ghost-${channel.key}`}>
              <span ref={ghostLabelRef}>—</span>
            </span>
          )}
          <span style={{ color: stroke }}>
            <span ref={labelRef}>—</span>
            <span className="opacity-50 ml-0.5">{channel.unit}</span>
          </span>
        </span>
      </div>
      <svg
        ref={svgRef}
        viewBox={`0 0 ${windowSec * X_SCALE} ${VH}`}
        preserveAspectRatio="none"
        className="block w-full flex-1 min-h-0"
      >
        {precomp.dGhost && (
          <path d={precomp.dGhost} fill="none" stroke={ghostColor} strokeOpacity={0.75} strokeWidth={1.2} strokeDasharray="3 2" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
        )}
        <path d={precomp.d} fill="none" stroke={stroke} strokeWidth={1.2} strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
        <line ref={cursorRef} x1={0} y1={0} x2={0} y2={VH} stroke="#ffffff" strokeOpacity={0.55} strokeWidth={1} strokeDasharray="2 2" vectorEffect="non-scaling-stroke" />
      </svg>
    </div>
  );
});

// Live value text — used in the sidebar chart headers. Subscribes to the
// shared ticker so the number updates at full video framerate without
// causing parent re-renders.
const LiveValueLabel = React.memo(function LiveValueLabel({ samples, channel, subscribe, getTime, decimals }) {
  const ref = React.useRef(null);
  React.useEffect(() => {
    if (!subscribe || !samples) return;
    const arr = samples[channel.key];
    const t = samples.time_s;
    if (!Array.isArray(arr) || !Array.isArray(t)) return;
    const update = (tNow) => {
      if (!ref.current) return;
      let v;
      if (tNow <= t[0]) v = arr[0];
      else if (tNow >= t[t.length - 1]) v = arr[t.length - 1];
      else {
        let lo = 0, hi = t.length - 1;
        while (lo < hi - 1) { const m = (lo + hi) >> 1; if (t[m] <= tNow) lo = m; else hi = m; }
        const f = (tNow - t[lo]) / Math.max(t[lo + 1] - t[lo], 1e-9);
        v = arr[lo] * (1 - f) + arr[lo + 1] * f;
      }
      ref.current.textContent = Number.isFinite(v)
        ? `${v.toFixed(decimals)} ${channel.unit}`
        : '—';
    };
    if (typeof getTime === 'function') update(getTime());
    return subscribe(update);
  }, [samples, channel, subscribe, getTime, decimals]);
  return <span ref={ref} className="font-mono ml-auto" style={{ color: channel.color }}>—</span>;
});

// Sidebar GPS map with subscription-driven interpolated dot.
const SidebarGPS = React.memo(function SidebarGPS({ trackPath, samples, subscribe, getTime, isVbo }) {
  const dotRef = React.useRef(null);
  const precomp = React.useMemo(() => {
    if (!trackPath) return null;
    const lats = trackPath.lat, lons = trackPath.lon;
    const minLat = Math.min(...lats), maxLat = Math.max(...lats);
    const minLon = Math.min(...lons), maxLon = Math.max(...lons);
    const dLat = maxLat - minLat || 1e-9;
    const dLon = maxLon - minLon || 1e-9;
    const W = 200, H = 200, pad = 10;
    const flipX = isVbo && (minLon + maxLon) / 2 > 0;
    const proj = (lat, lon) => [
      flipX
        ? pad + ((maxLon - lon) / dLon) * (W - 2 * pad)
        : pad + ((lon - minLon) / dLon) * (W - 2 * pad),
      H - pad - ((lat - minLat) / dLat) * (H - 2 * pad),
    ];
    // Selected lap's projected sample arrays for the interpolated dot.
    let dotXs = null, dotYs = null;
    if (samples?.lat && samples?.lon) {
      dotXs = new Array(samples.lat.length);
      dotYs = new Array(samples.lat.length);
      for (let i = 0; i < samples.lat.length; i++) {
        const [px, py] = proj(samples.lat[i], samples.lon[i]);
        dotXs[i] = px; dotYs[i] = py;
      }
    }
    // Full-track path string.
    let d = '';
    const step = Math.max(1, Math.floor(lats.length / 600));
    for (let k = 0; k < lats.length; k += step) {
      const [x, y] = proj(lats[k], lons[k]);
      d += (k === 0 ? 'M' : 'L') + x.toFixed(1) + ',' + y.toFixed(1) + ' ';
    }
    return { d, W, H, dotXs, dotYs, t: samples?.time_s };
  }, [trackPath, samples, isVbo]);

  React.useEffect(() => {
    if (!precomp || !subscribe || !precomp.dotXs) return;
    const { dotXs, dotYs, t } = precomp;
    const hasTime = Array.isArray(t);
    const update = (tNow) => {
      if (!dotRef.current) return;
      let x, y;
      if (!hasTime || tNow <= t[0]) { x = dotXs[0]; y = dotYs[0]; }
      else if (tNow >= t[t.length - 1]) { x = dotXs[t.length - 1]; y = dotYs[t.length - 1]; }
      else {
        let lo = 0, hi = t.length - 1;
        while (lo < hi - 1) { const m = (lo + hi) >> 1; if (t[m] <= tNow) lo = m; else hi = m; }
        const f = (tNow - t[lo]) / Math.max(t[lo + 1] - t[lo], 1e-9);
        x = dotXs[lo] * (1 - f) + dotXs[lo + 1] * f;
        y = dotYs[lo] * (1 - f) + dotYs[lo + 1] * f;
      }
      dotRef.current.setAttribute('cx', x);
      dotRef.current.setAttribute('cy', y);
    };
    if (typeof getTime === 'function') update(getTime());
    return subscribe(update);
  }, [precomp, subscribe, getTime]);

  if (!precomp) return <div className="text-xs text-gray-500 text-center py-8">Sin GPS</div>;
  return (
    <svg viewBox={`0 0 ${precomp.W} ${precomp.H}`} className="w-full h-auto bg-neutral-950 rounded" data-testid="replay-gps">
      <path d={precomp.d} fill="none" stroke="#60a5fa" strokeWidth={2} />
      <circle ref={dotRef} cx={-10} cy={-10} r={6} fill="#ef4444" stroke="#fff" strokeWidth={1.5} data-testid="replay-gps-dot" />
    </svg>
  );
});

// KPI tile (single big value). Subscribes to the shared ticker — no
// re-renders during playback.
const OverlayKPI = React.memo(function OverlayKPI({
  samples, channel, color, subscribe, getTime,
  ghostSamples = null, ghostColor = '#ffffff',
}) {
  const labelRef = React.useRef(null);
  const ghostLabelRef = React.useRef(null);
  const stroke = color || channel.color;
  const arr = samples?.[channel.key];
  const t = samples?.time_s;

  React.useEffect(() => {
    if (!Array.isArray(arr) || !Array.isArray(t) || !subscribe) return;
    const decimals = channel.key === 'rpm' ? 0 : 1;
    const update = (tNow) => {
      if (!labelRef.current) return;
      let v;
      if (tNow <= t[0]) v = arr[0];
      else if (tNow >= t[t.length - 1]) v = arr[t.length - 1];
      else {
        let lo = 0, hi = t.length - 1;
        while (lo < hi - 1) { const m = (lo + hi) >> 1; if (t[m] <= tNow) lo = m; else hi = m; }
        const f = (tNow - t[lo]) / Math.max(t[lo + 1] - t[lo], 1e-9);
        v = arr[lo] * (1 - f) + arr[lo + 1] * f;
      }
      labelRef.current.textContent = Number.isFinite(v) ? v.toFixed(decimals) : '—';
      if (ghostLabelRef.current && ghostSamples) {
        const dHere = valueAt(samples, 'distance_m', tNow);
        const gv = (dHere != null) ? valueAtDistance(ghostSamples, channel.key, dHere) : null;
        ghostLabelRef.current.textContent = Number.isFinite(gv) ? gv.toFixed(decimals) : '—';
      }
    };
    if (typeof getTime === 'function') update(getTime());
    return subscribe(update);
  }, [arr, t, subscribe, getTime, channel.key, ghostSamples, samples]);

  return (
    <div
      className="w-full h-full bg-black/65 backdrop-blur rounded border border-white/10 px-2 py-1 select-none flex flex-col justify-center"
      data-testid={`overlay-kpi-${channel.key}`}
    >
      <div className="text-[9px] uppercase opacity-60 text-white truncate">{channel.label}</div>
      <div className="font-mono leading-tight" style={{ color: stroke, fontSize: 'clamp(14px, 2.4vw, 28px)' }}>
        <span ref={labelRef}>—</span>
        <span className="text-[10px] opacity-50 ml-0.5">{channel.unit}</span>
      </div>
      {ghostSamples && (
        <div
          className="font-mono leading-tight"
          style={{ color: ghostColor, fontSize: 'clamp(9px, 1.4vw, 14px)', opacity: 0.85 }}
          data-testid={`overlay-kpi-ghost-${channel.key}`}
        >
          <span ref={ghostLabelRef}>—</span>
          <span className="text-[9px] opacity-50 ml-0.5">{channel.unit}</span>
        </div>
      )}
    </div>
  );
});

// Track map (fluid) — precompute the full GPS path; per-frame work goes
// straight to the DOM via a ref so the parent doesn't re-render.
const TrackMapOverlay = React.memo(function TrackMapOverlay({ samples, color, subscribe, getTime }) {
  const dotRef = React.useRef(null);
  const precomp = React.useMemo(() => {
    const lats = samples?.lat, lons = samples?.lon, t = samples?.time_s;
    if (!Array.isArray(lats) || !Array.isArray(lons) || lats.length < 2) return null;
    const minLat = Math.min(...lats), maxLat = Math.max(...lats);
    const minLon = Math.min(...lons), maxLon = Math.max(...lons);
    const dLat = maxLat - minLat || 1e-9;
    const dLon = maxLon - minLon || 1e-9;
    const VW = 100, VH = 100, pad = 4;
    const proj = (lat, lon) => [
      pad + ((lon - minLon) / dLon) * (VW - 2 * pad),
      VH - pad - ((lat - minLat) / dLat) * (VH - 2 * pad),
    ];
    let d = '';
    const step = Math.max(1, Math.floor(lats.length / 400));
    const proj_xs = new Array(lats.length);
    const proj_ys = new Array(lats.length);
    for (let i = 0; i < lats.length; i++) {
      const [x, y] = proj(lats[i], lons[i]);
      proj_xs[i] = x; proj_ys[i] = y;
    }
    for (let i = 0; i < lats.length; i += step) {
      d += (i === 0 ? 'M' : 'L') + proj_xs[i].toFixed(1) + ',' + proj_ys[i].toFixed(1) + ' ';
    }
    return { d, VW, VH, t, proj_xs, proj_ys };
  }, [samples]);

  React.useEffect(() => {
    if (!precomp || !subscribe) return;
    const { t, proj_xs, proj_ys } = precomp;
    const hasTime = Array.isArray(t);
    const update = (tNow) => {
      if (!dotRef.current) return;
      // Interpolate between adjacent GPS samples so the dot moves smoothly
      // even when telemetry was captured at a low rate (e.g. 10 Hz VBO).
      let x, y;
      if (!hasTime || tNow <= t[0]) { x = proj_xs[0]; y = proj_ys[0]; }
      else if (tNow >= t[t.length - 1]) { x = proj_xs[t.length - 1]; y = proj_ys[t.length - 1]; }
      else {
        let lo = 0, hi = t.length - 1;
        while (lo < hi - 1) { const m = (lo + hi) >> 1; if (t[m] <= tNow) lo = m; else hi = m; }
        const f = (tNow - t[lo]) / Math.max(t[lo + 1] - t[lo], 1e-9);
        x = proj_xs[lo] * (1 - f) + proj_xs[lo + 1] * f;
        y = proj_ys[lo] * (1 - f) + proj_ys[lo + 1] * f;
      }
      dotRef.current.setAttribute('cx', x);
      dotRef.current.setAttribute('cy', y);
    };
    if (typeof getTime === 'function') update(getTime());
    return subscribe(update);
  }, [precomp, subscribe, getTime]);

  if (!precomp) return null;
  const { d, VW, VH } = precomp;
  const trackColor = color || '#60a5fa';

  return (
    <div className="w-full h-full bg-black/65 backdrop-blur rounded border border-white/10 p-1" data-testid="overlay-map">
      <svg viewBox={`0 0 ${VW} ${VH}`} preserveAspectRatio="xMidYMid meet" className="w-full h-full">
        <path d={d} fill="none" stroke={trackColor} strokeWidth={1.5} vectorEffect="non-scaling-stroke" />
        <circle ref={dotRef} cx={-10} cy={-10} r={2.5} fill="#ef4444" stroke="#fff" strokeWidth={0.6} vectorEffect="non-scaling-stroke" />
      </svg>
    </div>
  );
});

// Lap-time overlay — shows the elapsed time in the current lap, formatted
// as M:SS.mmm. Drives directly from the telemetry `time_s` axis: since each
// lap's samples start at t=0 and end at lap_time_s, the "now" value is
// literally the current sync-adjusted time clamped to that range.
const OverlayLapTime = React.memo(function OverlayLapTime({ samples, color, subscribe, getTime }) {
  const labelRef = React.useRef(null);
  const t = samples?.time_s;
  const maxT = (Array.isArray(t) && t.length > 0) ? t[t.length - 1] : null;

  React.useEffect(() => {
    if (!subscribe) return;
    const update = (tNow) => {
      if (!labelRef.current) return;
      let tt = Math.max(0, tNow);
      if (maxT != null) tt = Math.min(tt, maxT);
      const m = Math.floor(tt / 60);
      const s = tt - m * 60;
      labelRef.current.textContent = `${m}:${s.toFixed(3).padStart(6, '0')}`;
    };
    if (typeof getTime === 'function') update(getTime());
    return subscribe(update);
  }, [subscribe, getTime, maxT]);

  return (
    <div
      className="w-full h-full bg-black/65 backdrop-blur rounded border border-white/10 px-2 py-1 select-none flex flex-col justify-center"
      data-testid="overlay-laptime"
    >
      <div className="text-[9px] uppercase opacity-60 text-white truncate">Lap Time</div>
      <div className="font-mono leading-tight" style={{ color: color || '#fde047', fontSize: 'clamp(14px, 2.4vw, 28px)' }}>
        <span ref={labelRef}>0:00.000</span>
      </div>
    </div>
  );
});

// Branded logo watermark — non-removable. Lives over the video as advertising.
function OverlayLogo() {
  return (
    <div
      className="w-full h-full pointer-events-none select-none flex items-center justify-center"
      data-testid="overlay-logo"
    >
      <img
        src="/branding/datapro-logo.png"
        alt="DATAPRO"
        draggable={false}
        className="w-full h-full object-contain rounded bg-white"
      />
    </div>
  );
}

// Delta vs ghost — live difference in lap time at the CURRENT circuit
// position. Positive = main is slower than ghost (RED). Negative = faster
// (GREEN). Computed via distance bridge: at video time t we ask "how much
// time had the main lap accumulated by distance D vs the ghost lap".
const OverlayDelta = React.memo(function OverlayDelta({ samples, ghostSamples, subscribe, getTime }) {
  const labelRef = React.useRef(null);

  React.useEffect(() => {
    if (!subscribe) return;
    const update = (tNow) => {
      if (!labelRef.current) return;
      if (!samples || !ghostSamples) {
        labelRef.current.textContent = '—';
        labelRef.current.style.color = '#9ca3af';
        return;
      }
      const dHere = valueAt(samples, 'distance_m', tNow);
      if (dHere == null) {
        labelRef.current.textContent = '—';
        return;
      }
      // For the ghost: at this same distance, when (lap-elapsed) was the
      // ghost car?  We invert ghost.distance_m → ghost.time_s.
      const gT = valueAtDistance(ghostSamples, 'time_s', dHere);
      if (!Number.isFinite(gT)) {
        labelRef.current.textContent = '—';
        return;
      }
      // Main "elapsed in lap" at this same distance is simply tNow (since
      // samples.time_s starts at 0 at lap start). Delta = main - ghost:
      // positive means main took longer to reach this point.
      const delta = tNow - gT;
      const sign = delta > 0 ? '+' : (delta < 0 ? '−' : '±');
      labelRef.current.textContent = `${sign}${Math.abs(delta).toFixed(3)}`;
      labelRef.current.style.color = delta > 0.05 ? '#ef4444' : (delta < -0.05 ? '#22c55e' : '#fbbf24');
    };
    if (typeof getTime === 'function') update(getTime());
    return subscribe(update);
  }, [samples, ghostSamples, subscribe, getTime]);

  return (
    <div
      className="w-full h-full bg-black/65 backdrop-blur rounded border border-white/10 px-2 py-1 select-none flex flex-col justify-center"
      data-testid="overlay-delta"
    >
      <div className="text-[9px] uppercase opacity-60 text-white truncate">Δ vs Ghost</div>
      <div className="font-mono leading-tight" style={{ fontSize: 'clamp(14px, 2.4vw, 28px)' }}>
        <span ref={labelRef}>—</span>
        <span className="text-[10px] opacity-50 ml-0.5">s</span>
      </div>
    </div>
  );
});

// Default overlay layout (percentages of the video area).
const LOGO_WIDGET = {
  id: 'datapro-logo',
  type: 'logo',
  channel: null,
  color: null,
  x: 78, y: 86, w: 20, h: 11,
  visible: true,
  locked: true,
};

const DEFAULT_OVERLAY_LAYOUT = [
  { id: 'kpi-speed',   type: 'kpi',       channel: 'speed_kmh', color: '#60a5fa', x: 1,  y: 2,  w: 13, h: 13, visible: true },
  { id: 'kpi-rpm',     type: 'kpi',       channel: 'rpm',       color: '#a78bfa', x: 15, y: 2,  w: 13, h: 13, visible: true },
  { id: 'kpi-laptime', type: 'lap_time',  channel: null,        color: '#fde047', x: 29, y: 2,  w: 17, h: 13, visible: true },
  { id: 'spark-speed', type: 'sparkline', channel: 'speed_kmh', color: '#60a5fa', x: 66, y: 2,  w: 32, h: 14, visible: true },
  { id: 'spark-rpm',   type: 'sparkline', channel: 'rpm',       color: '#a78bfa', x: 66, y: 18, w: 32, h: 14, visible: true },
  { id: 'spark-latg',  type: 'sparkline', channel: 'lat_g',     color: '#fbbf24', x: 66, y: 34, w: 32, h: 14, visible: true },
  { id: 'spark-longg', type: 'sparkline', channel: 'long_g',    color: '#4ade80', x: 66, y: 50, w: 32, h: 14, visible: true },
  { id: 'map',         type: 'map',       channel: null,        color: '#60a5fa', x: 1,  y: 70, w: 20, h: 28, visible: false },
  LOGO_WIDGET,
];

function valueAt(samples, key, telemetryTime) {
  if (!samples || !Array.isArray(samples.time_s) || !Array.isArray(samples[key])) return null;
  const t = samples.time_s;
  const arr = samples[key];
  if (telemetryTime <= t[0]) return arr[0];
  if (telemetryTime >= t[t.length - 1]) return arr[t.length - 1];
  let lo = 0, hi = t.length - 1;
  while (lo < hi - 1) {
    const mid = (lo + hi) >> 1;
    if (t[mid] <= telemetryTime) lo = mid; else hi = mid;
  }
  const f = (telemetryTime - t[lo]) / Math.max(t[hi] - t[lo], 1e-9);
  return arr[lo] * (1 - f) + arr[hi] * f;
}

// Look up a channel value at a given distance (metres along the lap). Used
// for "ghost" comparison overlays: when the main lap is at distance D, we
// want to know what the comparison lap had at the same circuit position.
// Binary search on samples.distance_m. Returns null if data is missing.
function valueAtDistance(samples, key, distance) {
  if (!samples || !Array.isArray(samples.distance_m) || !Array.isArray(samples[key])) return null;
  const d = samples.distance_m;
  const arr = samples[key];
  if (distance <= d[0]) return arr[0];
  if (distance >= d[d.length - 1]) return arr[d.length - 1];
  let lo = 0, hi = d.length - 1;
  while (lo < hi - 1) {
    const mid = (lo + hi) >> 1;
    if (d[mid] <= distance) lo = mid; else hi = mid;
  }
  const f = (distance - d[lo]) / Math.max(d[hi] - d[lo], 1e-9);
  return arr[lo] * (1 - f) + arr[hi] * f;
}

// Small uniform button used in the inline sync bar. Keeps every fine-step
// control visually identical and a11y-labeled.
function FineBtn({ children, onClick, testid, title, disabled = false }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      title={title}
      className="h-7 px-2 rounded bg-neutral-800 hover:bg-neutral-700 disabled:opacity-40 text-gray-200 text-[11px] font-mono"
    >
      {children}
    </button>
  );
}

export default function ReplayStudio() {
  const { eventId, tandaKey, sessionId } = useParams();
  const navigate = useNavigate();
  // Mode "session" is the Pro flow: video is bound directly to a telemetry
  // session, no Pit Engineer event/tanda required. The legacy event/tanda
  // mode stays untouched for admins coming from the Pit Engineer.
  const mode = sessionId ? 'session' : 'tanda';
  const bindingPayload = mode === 'session'
    ? { session_id: sessionId }
    : { event_id: eventId, tanda_key: tandaKey };
  const [event, setEvent] = useState(null);
  const [telemetry, setTelemetry] = useState(null);
  const [replay, setReplay] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [externalUrl, setExternalUrl] = useState('');
  const [showExternal, setShowExternal] = useState(false);
  const [selectedLap, setSelectedLap] = useState(null);
  const [videoTime, setVideoTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showOverlayCharts, setShowOverlayCharts] = useState(true);
  const [overlayLayout, setOverlayLayout] = useState(DEFAULT_OVERLAY_LAYOUT);
  const [editLayout, setEditLayout] = useState(false);
  // --- Ghost (comparison telemetry) ---------------------------------------
  // The "ghost" is a second telemetry session whose channels are projected
  // ONTO the main lap by circuit distance (so the same point of track lines
  // up regardless of lap time). This enables MoTeC-style A/B overlay and
  // live delta-time vs a reference (e.g., compare Final to Qualy lap).
  const [ghostSessions, setGhostSessions] = useState([]);   // list of user's other sessions
  const [ghostSessionId, setGhostSessionId] = useState(''); // '' = no ghost
  const [ghostTelemetry, setGhostTelemetry] = useState(null);
  const [ghostLapNumber, setGhostLapNumber] = useState(null);
  const [ghostColor, setGhostColor] = useState('#ffffff');
  const [ghostEnabled, setGhostEnabled] = useState(true);   // master on/off toggle
  // Bumped whenever the backend finishes a transcode/remux so we force the
  // <video> element to re-fetch (otherwise iOS Safari keeps the cached
  // pre-transcode failure state and never retries the new file).
  const [videoVersion, setVideoVersion] = useState(0);
  const videoBoxRef = useRef(null);
  const layoutSaveTimer = useRef(null);

  // --- High-rate ticker -----------------------------------------------------
  // We expose a `subscribe(cb)` API to overlay children. The callback fires
  // EVERY video frame with the current presentation time. Overlays mutate the
  // DOM directly via refs so React never re-renders during playback. This is
  // the key to keeping the data sample perfectly glued to the video frame.
  const timeRef = useRef(0);                    // latest mediaTime + offset
  const subscribersRef = useRef(new Set());
  // Force-tick: imperative function that re-fires every subscriber with the
  // current `video.currentTime + offset`. Lives behind a ref so any handler
  // (offset slider, fine ±10ms buttons, "Sync acá") can push an instant
  // overlay refresh without waiting for the next video frame — critical
  // when the video is paused.
  const forceTickRef = useRef(null);
  // Auto-detected frame interval (s) — populated from rVFC metadata so step
  // navigation matches the actual video framerate (24 / 25 / 29.97 / 30 / 60).
  const frameDurationRef = useRef(1 / 30);
  const [playbackRate, setPlaybackRate] = useState(1);
  const subscribe = React.useCallback((cb) => {
    subscribersRef.current.add(cb);
    return () => { subscribersRef.current.delete(cb); };
  }, []);
  const getTime = React.useCallback(() => timeRef.current, []);
  const videoRef = useRef(null);
  const fileRef = useRef(null);

  // Pick a sensible lap to display given a stored lap_number that might be
  // stale after a recut renumbered the session's laps.
  const _resolveLap = (telem, storedLap) => {
    if (!telem) return storedLap || null;
    const exists = storedLap && telem.laps?.some((l) => l.lap_number === storedLap);
    if (exists) return storedLap;
    return telem.fastest_lap_number || telem.laps?.[0]?.lap_number || null;
  };

  // -- Diagnostics --------------------------------------------------------- //
  // Hits /api/replay/_diag/{id} and surfaces the first failing step so support
  // (and the engineer) can pinpoint why a video won't stream. Bound to the
  // toast "Diagnóstico" action and to a manual button in the player header.
  const runDiagnostics = React.useCallback(async () => {
    if (!replay?.id) {
      toast.error('No hay replay activo todavía.');
      return;
    }
    const t = toast.loading('Corriendo diagnóstico…');
    try {
      const r = await axios.get(`${API}/replay/_diag/${replay.id}`, { withCredentials: true });
      const report = r.data || {};
      const steps = report.steps || [];
      const failed = steps.find((s) => s.ok === false || s.exists === false);
      const lines = steps.map((s) => {
        const head = s.step;
        if (s.step === 'token_validation')   return `${s.ok ? '✓' : '✗'} ${head} · src=${s.source} · user=${s.user_id || '-'}`;
        if (s.step === 'db_lookup')          return `${s.ok ? '✓' : '✗'} ${head} · ${s.video_url || s.error}`;
        if (s.step === 'filesystem_check')   return `${s.exists ? '✓' : '✗'} ${head} · ${s.expected_path} · ${s.exists ? `${s.actual_size}B` : 'MISSING'}`;
        if (s.step === 'streaming_url')      return `→ ${head} · ${s.url}${s.WARNING ? ' · ⚠ ' + s.WARNING : ''}`;
        return `· ${head}`;
      });
      const ok = report.overall_ok;
      toast.dismiss(t);
      // Show as a long toast in fixed-width so engineers can copy it for support.
      toast(
        ok ? '✓ Diagnóstico OK' : `✗ Falló: ${failed?.step || 'desconocido'}`,
        {
          duration: 30000,
          description: lines.join('\n') + (ok ? '' : `\n\nffmpeg=${report.ffmpeg_available} · ffprobe=${report.ffprobe_available}`),
          style: { whiteSpace: 'pre-wrap', fontFamily: 'ui-monospace, monospace', fontSize: '11px' },
        },
      );
      // Also dump to console for copy-paste support.
      // eslint-disable-next-line no-console
      console.info('[replay diag]', report);
    } catch (err) {
      toast.dismiss(t);
      toast.error(`Diagnóstico falló: ${err?.response?.status || ''} ${err?.message || ''}`);
    }
  }, [replay?.id]);


  // -- Runtime health (post-deploy alert) ---------------------------------- //
  // Probes /api/replay/_health on mount. If ffmpeg/ffprobe are missing on
  // the host (typical after a redeploy of a slim image) or the storage
  // round-trip fails, we surface a visible banner to whoever is running the
  // session — long before they hit Upload and discover the issue mid-event.
  const [health, setHealth] = useState(null);
  useEffect(() => {
    let cancelled = false;
    axios.post(`${API}/replay/_health`, {}, { withCredentials: true })
      .then((r) => { if (!cancelled) setHealth(r.data); })
      .catch((err) => {
        if (cancelled) return;
        setHealth({ status: 'FAIL', error: `Health endpoint inaccesible: ${err?.response?.status || err?.message}` });
      });
    return () => { cancelled = true; };
  }, []);




  // -- Initial load -------------------------------------------------------- //
  useEffect(() => {
    const load = async () => {
      try {
        if (mode === 'session') {
          // Pro flow — telemetry session is the primary handle.
          let ts = null;
          try {
            const tsRes = await axios.get(`${API}/telemetry/sessions/${sessionId}?include_samples=true`, { withCredentials: true });
            ts = tsRes.data;
            setTelemetry(ts);
            setEvent({
              pilot: ts.pilot || '',
              circuit: ts.circuit || '',
              category: ts.category || '',
              name: ts.name,
            });
          } catch (e) {
            toast.error(e?.response?.data?.detail || 'No se pudo cargar la sesión DAQ');
          }
          const rep = await axios.get(`${API}/replay/by-session/${sessionId}`, {
            withCredentials: true,
          });
          setReplay(rep.data.replay);
          const resolved = _resolveLap(ts, rep.data.replay?.lap_number);
          if (rep.data.replay?.lap_number && ts && !ts.laps?.some((l) => l.lap_number === rep.data.replay.lap_number)) {
            toast.message('La vuelta asociada al video ya no existe (recut). Mostrando la vuelta rápida.');
          }
          setSelectedLap(resolved);
        } else {
          // Legacy admin flow — comes from Pit Engineer with event+tanda.
          const ev = await axios.get(`${API}/pit/events/${eventId}`, { withCredentials: true });
          setEvent(ev.data);
          const tanda = ev.data?.tandas?.[tandaKey] || {};
          const sid = tanda?.telemetry_link?.session_id;
          let ts = null;
          if (sid) {
            try {
              const tsRes = await axios.get(`${API}/telemetry/sessions/${sid}?include_samples=true`, { withCredentials: true });
              ts = tsRes.data;
              setTelemetry(ts);
            } catch (e) {
              console.warn('Sin telemetría vinculada:', e?.response?.data);
            }
          }
          const rep = await axios.get(`${API}/replay/by-tanda`, {
            params: { event_id: eventId, tanda_key: tandaKey },
            withCredentials: true,
          });
          setReplay(rep.data.replay);
          const resolved = _resolveLap(ts, rep.data.replay?.lap_number);
          if (rep.data.replay?.lap_number && ts && !ts.laps?.some((l) => l.lap_number === rep.data.replay.lap_number)) {
            toast.message('La vuelta asociada al video ya no existe (recut). Mostrando la vuelta rápida.');
          }
          setSelectedLap(resolved);
        }
      } catch (err) {
        toast.error(err.response?.data?.detail || 'No se pudo cargar el replay');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [eventId, tandaKey, sessionId]);

  // -- Chunked upload ------------------------------------------------------ //
  const uploadFile = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().match(/\.(mp4|mov|m4v)$/)) {
      toast.error('Solo se aceptan .mp4 o .mov');
      return;
    }
    setUploading(true);
    setUploadPct(0);
    try {
      const init = await axios.post(`${API}/replay/upload/init`, {
        ...bindingPayload,
        filename: file.name, size: file.size,
        lap_number: selectedLap,
      }, { withCredentials: true });
      const { upload_id, chunk_size } = init.data;
      let sent = 0;
      for (let off = 0; off < file.size; off += chunk_size) {
        const blob = file.slice(off, Math.min(off + chunk_size, file.size));
        await axios.post(
          `${API}/replay/upload/chunk/${upload_id}`,
          blob,
          {
            withCredentials: true,
            headers: { 'Content-Type': 'application/octet-stream' },
          },
        );
        sent += blob.size;
        setUploadPct(Math.round((sent / file.size) * 100));
      }
      const complete = await axios.post(`${API}/replay/upload/complete`, {
        upload_id, sync_offset: 0,
      }, { withCredentials: true });
      setReplay(complete.data.replay);
      toast.success('Video subido');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Falló la subida');
    } finally {
      setUploading(false);
    }
  };

  const saveExternal = async () => {
    if (!externalUrl.trim()) return;
    try {
      const res = await axios.post(`${API}/replay/external`, {
        ...bindingPayload,
        video_url: externalUrl.trim(),
        sync_offset: 0, lap_number: selectedLap,
      }, { withCredentials: true });
      setReplay(res.data.replay);
      setShowExternal(false);
      setExternalUrl('');
      toast.success('URL guardada');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo guardar la URL');
    }
  };

  const deleteReplay = async () => {
    if (!replay) return;
    if (!window.confirm('¿Borrar este replay (video + offset)?')) return;
    try {
      await axios.delete(`${API}/replay/${replay.id}`, { withCredentials: true });
      setReplay(null);
      toast.success('Replay borrado');
    } catch (err) {
      toast.error('No se pudo borrar');
    }
  };

  // -- Sync offset (persisted) -------------------------------------------- //
  const setOffset = async (newOffset) => {
    if (!replay) return;
    // Optimistic local update so the slider value and the overlays repaint
    // *immediately* — without waiting for the PATCH round-trip. The ticker
    // useEffect reads `replay.sync_offset` so we also update it locally
    // before persisting.
    setReplay((prev) => prev ? { ...prev, sync_offset: newOffset } : prev);
    // Push an instant tick so KPIs / sparklines / GPS / cursors all redraw
    // even when the video is paused. Without this the overlays would stay
    // stale until the next `requestVideoFrameCallback` (i.e. forever, in pause).
    if (forceTickRef.current) forceTickRef.current();
    try {
      const res = await axios.patch(`${API}/replay/${replay.id}`, {
        sync_offset: newOffset,
      }, { withCredentials: true });
      setReplay(res.data.replay);
    } catch (err) {
      toast.error('No se pudo guardar el offset');
    }
  };

  const setLapNumber = async (lapNum) => {
    setSelectedLap(lapNum);
    if (replay) {
      try {
        await axios.patch(`${API}/replay/${replay.id}`, { lap_number: lapNum }, { withCredentials: true });
      } catch (e) { /* non-fatal */ }
    }
  };

  // -- Player ticker ------------------------------------------------------- //
  // Prefer requestVideoFrameCallback. The hot path here goes to the DOM
  // directly via `subscribersRef`; React state (videoTime) is updated at a
  // much lower rate (~5fps) just so the time pill / play state stays current.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    let cancelled = false;
    let rvfcId = null;
    let rafId = null;
    let lastSlowUpdate = 0;
    const fire = (t) => {
      timeRef.current = t + (replay?.sync_offset ?? 0);
      subscribersRef.current.forEach((cb) => {
        try { cb(timeRef.current); } catch (_) { /* keep going */ }
      });
      const nowMs = (typeof performance !== 'undefined' ? performance.now() : Date.now());
      if (nowMs - lastSlowUpdate > 200) {
        lastSlowUpdate = nowMs;
        setVideoTime(t);
      }
    };
    // Expose a way to repaint overlays without depending on the next frame
    // callback. Called from offset / frame-step / "Sync acá" handlers so
    // changes feel instant even when the video is paused.
    forceTickRef.current = () => fire(v.currentTime || 0);
    if (typeof v.requestVideoFrameCallback === 'function') {
      let lastMediaTime = null;
      let lastPresentedFrames = null;
      const onFrame = (_now, metadata) => {
        if (cancelled) return;
        const t = metadata && Number.isFinite(metadata.mediaTime)
          ? metadata.mediaTime
          : (v.currentTime || 0);
        // Estimate frame duration from consecutive presentedFrames deltas.
        // Works for VFR videos too — we just take a running min.
        if (metadata && Number.isFinite(metadata.mediaTime)
            && lastMediaTime != null && lastPresentedFrames != null) {
          const dFrames = metadata.presentedFrames - lastPresentedFrames;
          const dMedia = metadata.mediaTime - lastMediaTime;
          if (dFrames > 0 && dMedia > 0 && dMedia < 1) {
            const fd = dMedia / dFrames;
            // Lock-in once we've seen a stable value.
            if (fd > 1 / 240 && fd < 1 / 12) {
              frameDurationRef.current = fd;
            }
          }
        }
        if (metadata) {
          lastMediaTime = metadata.mediaTime;
          lastPresentedFrames = metadata.presentedFrames;
        }
        fire(t);
        rvfcId = v.requestVideoFrameCallback(onFrame);
      };
      rvfcId = v.requestVideoFrameCallback(onFrame);
    } else {
      const tick = () => {
        if (cancelled) return;
        fire(v.currentTime || 0);
        rafId = requestAnimationFrame(tick);
      };
      rafId = requestAnimationFrame(tick);
    }
    return () => {
      cancelled = true;
      if (rvfcId != null && typeof v.cancelVideoFrameCallback === 'function') {
        v.cancelVideoFrameCallback(rvfcId);
      }
      if (rafId != null) cancelAnimationFrame(rafId);
    };
  }, [replay?.id, replay?.sync_offset]);

  // Keep video.playbackRate in sync with the UI state.
  useEffect(() => {
    if (videoRef.current) videoRef.current.playbackRate = playbackRate;
  }, [playbackRate, replay?.id]);

  // -- Keyboard shortcuts ------------------------------------------------- //
  // Professional analysis tool conventions:
  //   Space               → play / pause
  //   ← / →               → previous / next frame (auto-detected fps)
  //   Shift + ← / →       → ±100 ms
  //   Alt   + ← / →       → ±10 ms
  //   Shift + Space       → toggle 0.25x slow-motion
  useEffect(() => {
    if (!replay) return;
    const onKey = (e) => {
      const v = videoRef.current;
      if (!v) return;
      // Don't capture keys while the user is typing in an input/textarea.
      const tag = (e.target?.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || e.target?.isContentEditable) return;
      const fd = frameDurationRef.current || 1 / 30;
      let handled = true;
      if (e.code === 'Space' && !e.shiftKey) {
        if (v.paused) v.play(); else v.pause();
      } else if (e.code === 'Space' && e.shiftKey) {
        setPlaybackRate((r) => (r === 1 ? 0.25 : 1));
      } else if (e.code === 'ArrowLeft' && e.altKey) {
        v.pause(); v.currentTime = Math.max(0, v.currentTime - 0.01);
      } else if (e.code === 'ArrowRight' && e.altKey) {
        v.pause(); v.currentTime = Math.min(v.duration || 0, v.currentTime + 0.01);
      } else if (e.code === 'ArrowLeft' && e.shiftKey) {
        v.pause(); v.currentTime = Math.max(0, v.currentTime - 0.1);
      } else if (e.code === 'ArrowRight' && e.shiftKey) {
        v.pause(); v.currentTime = Math.min(v.duration || 0, v.currentTime + 0.1);
      } else if (e.code === 'ArrowLeft') {
        v.pause(); v.currentTime = Math.max(0, v.currentTime - fd);
      } else if (e.code === 'ArrowRight') {
        v.pause(); v.currentTime = Math.min(v.duration || 0, v.currentTime + fd);
      } else {
        handled = false;
      }
      if (handled) e.preventDefault();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [replay?.id]);

  // Poll while a server-side transcode is in progress.
  useEffect(() => {
    if (replay?.transcoding_status !== 'in_progress') return;
    const id = setInterval(async () => {
      try {
        const rep = mode === 'session'
          ? await axios.get(`${API}/replay/by-session/${sessionId}`, { withCredentials: true })
          : await axios.get(`${API}/replay/by-tanda`, {
              params: { event_id: eventId, tanda_key: tandaKey },
              withCredentials: true,
            });
        if (rep.data.replay && rep.data.replay.transcoding_status !== 'in_progress') {
          setReplay(rep.data.replay);
          if (rep.data.replay.transcoding_status === 'done') {
            toast.success('Video listo — recargando reproductor…');
            // Bump the cache-bust so the <video> element re-fetches with the
            // newly-remuxed file (otherwise iOS Safari sticks on the cached
            // pre-transcode error and never retries).
            setVideoVersion((v) => v + 1);
            // Belt-and-suspenders: explicitly call load() in case the src
            // change alone doesn't trigger a re-fetch.
            setTimeout(() => {
              const el = videoRef.current;
              if (el) {
                try { el.load(); } catch (_) { /* ignore */ }
              }
            }, 80);
          }
        } else if (rep.data.replay) {
          setReplay(rep.data.replay);
        }
      } catch (e) { /* keep polling */ }
    }, 4000);
    return () => clearInterval(id);
  }, [replay?.transcoding_status, replay?.id, mode, sessionId, eventId, tandaKey]);

  // Load saved overlay layout from the server (default if none). The DATAPRO
  // logo widget is always force-injected — it's a branded watermark and isn't
  // user-removable.
  useEffect(() => {
    const saved = replay?.overlay_layout;
    if (Array.isArray(saved) && saved.length) {
      const withLogo = saved.some((w) => w.type === 'logo')
        ? saved.map((w) => (w.type === 'logo' ? { ...w, locked: true, visible: true } : w))
        : [...saved, LOGO_WIDGET];
      setOverlayLayout(withLogo);
    } else {
      setOverlayLayout(DEFAULT_OVERLAY_LAYOUT);
    }
  }, [replay?.id]);

  // Fetch the list of telemetry sessions the user owns so we can offer them
  // as "ghost" comparisons. Excludes the currently-loaded session so the user
  // doesn't compare a lap against itself by accident.
  useEffect(() => {
    if (!telemetry?.session_id) return;
    let cancelled = false;
    axios.get(`${API}/telemetry/sessions`, { withCredentials: true })
      .then((res) => {
        if (cancelled) return;
        const list = Array.isArray(res.data?.sessions) ? res.data.sessions : (Array.isArray(res.data) ? res.data : []);
        setGhostSessions(list.filter((s) => s.session_id !== telemetry.session_id));
      })
      .catch(() => { if (!cancelled) setGhostSessions([]); });
    return () => { cancelled = true; };
  }, [telemetry?.session_id]);

  // Whenever the user picks a different ghost session, hydrate its full
  // payload (with samples). Default the ghost lap to the fastest of that
  // session. When ghostSessionId is empty we don't clear synchronously — we
  // gate via the `ghostSamples` memo below (it requires session_id to match
  // the currently selected one) so stale data is invisible.
  useEffect(() => {
    if (!ghostSessionId) return;
    let cancelled = false;
    axios.get(`${API}/telemetry/sessions/${ghostSessionId}?include_samples=true`, { withCredentials: true })
      .then((res) => {
        if (cancelled) return;
        const t = res.data;
        setGhostTelemetry(t);
        // Pick fastest lap by default
        const fast = (t?.laps || []).reduce((best, l) => (
          best == null || l.lap_time_s < best.lap_time_s ? l : best
        ), null);
        setGhostLapNumber(fast?.lap_number || null);
      })
      .catch(() => { if (!cancelled) { setGhostTelemetry(null); setGhostLapNumber(null); } });
    return () => { cancelled = true; };
  }, [ghostSessionId]);

  // Debounced persistence (don't spam the API while dragging).
  const persistLayout = (next) => {
    if (!replay?.id) return;
    if (layoutSaveTimer.current) clearTimeout(layoutSaveTimer.current);
    layoutSaveTimer.current = setTimeout(() => {
      axios.patch(`${API}/replay/${replay.id}`, { overlay_layout: next }, { withCredentials: true })
        .catch(() => {});
    }, 600);
  };

  const updateWidget = (id, partial) => {
    setOverlayLayout((cur) => {
      const next = cur.map((w) => {
        if (w.id !== id) return w;
        // Logo watermark cannot be made invisible.
        if (w.type === 'logo') {
          const p = { ...partial };
          delete p.visible;
          return { ...w, ...p, visible: true, locked: true };
        }
        return { ...w, ...partial };
      });
      persistLayout(next);
      return next;
    });
  };

  const removeWidget = (id) => {
    setOverlayLayout((cur) => {
      const target = cur.find((w) => w.id === id);
      if (target?.type === 'logo') return cur; // logo watermark is permanent
      const next = cur.filter((w) => w.id !== id);
      persistLayout(next);
      return next;
    });
  };

  const addWidget = (type, channelKey) => {
    const id = `${type}-${channelKey || 'x'}-${Math.random().toString(36).slice(2, 6)}`;
    const ch = ALL_CHANNELS.find((c) => c.key === channelKey);
    const base = type === 'kpi'
      ? { x: 2, y: 2, w: 14, h: 12 }
      : type === 'lap_time'
        ? { x: 30, y: 2, w: 17, h: 13 }
        : type === 'delta'
          ? { x: 48, y: 2, w: 17, h: 13 }
          : type === 'map'
            ? { x: 2, y: 65, w: 22, h: 30 }
            : { x: 60, y: 60, w: 35, h: 16 };
    const defaultColor = type === 'lap_time' ? '#fde047' : (type === 'delta' ? '#ffffff' : (ch?.color || '#60a5fa'));
    const w = {
      id, type,
      channel: channelKey || null,
      color: defaultColor,
      ...base,
      visible: true,
    };
    setOverlayLayout((cur) => {
      const next = [...cur, w];
      persistLayout(next);
      return next;
    });
  };

  const resetLayout = () => {
    setOverlayLayout(DEFAULT_OVERLAY_LAYOUT);
    persistLayout(DEFAULT_OVERLAY_LAYOUT);
  };

  // -- Telemetry data for selected lap, indexed by `t` -------------------- //
  const lapData = useMemo(() => {
    if (!telemetry || !selectedLap) return null;
    return telemetry.laps.find((l) => l.lap_number === selectedLap) || null;
  }, [telemetry, selectedLap]);

  // Telemetry samples already include `time_s` (elapsed seconds within the
  // lap). No fallback needed.
  const samples = useMemo(() => lapData?.samples || null, [lapData]);

  // Ghost samples for the currently-selected ghost lap. Plumbed into the
  // OverlayKPI/Sparkline/Delta widgets as an optional second curve. Falls back
  // to null when ghost is disabled or unavailable.
  const ghostSamples = useMemo(() => {
    if (!ghostEnabled || !ghostSessionId || !ghostTelemetry || !ghostLapNumber) return null;
    // Guard against stale data: when the user clears or changes ghostSessionId
    // the previous payload remains in state until the async fetch lands. Only
    // expose it once the loaded telemetry matches the current selection.
    if (ghostTelemetry.session_id && ghostTelemetry.session_id !== ghostSessionId) return null;
    const lap = ghostTelemetry.laps?.find((l) => l.lap_number === ghostLapNumber);
    if (!lap?.samples) return null;
    // Both laps must expose distance_m for the projection to work.
    if (!Array.isArray(lap.samples.distance_m) || !Array.isArray(samples?.distance_m)) return null;
    return lap.samples;
  }, [ghostEnabled, ghostSessionId, ghostTelemetry, ghostLapNumber, samples]);

  // Warn if the user is comparing across different circuits — usually a
  // mistake. Match is fuzzy (case + trim) on the `circuit` metadata.
  const ghostCircuitMismatch = useMemo(() => {
    if (!ghostTelemetry || !telemetry) return false;
    const a = (telemetry.circuit || '').trim().toLowerCase();
    const b = (ghostTelemetry.circuit || '').trim().toLowerCase();
    if (!a || !b) return false;
    return a !== b;
  }, [telemetry, ghostTelemetry]);

  const offset = replay?.sync_offset ?? 0;
  const telemetryTime = videoTime + offset;

  // The "Trazado GPS" sidebar shows the full circuit shape. Using only the
  // selected lap can produce a half-drawn shape if the lap is mis-cut. We
  // build it from the LONGEST lap in the session (most likely a clean full
  // circuit) and apply the same X-flip convention as TelemetryPage so VBO
  // files (positive-west longitude) draw with east on the right.
  const trackPath = useMemo(() => {
    if (!telemetry?.laps?.length) return null;
    // Pick the lap with the most samples — that's usually a complete circuit.
    let best = telemetry.laps[0];
    for (const lap of telemetry.laps) {
      if ((lap.samples?.lat?.length || 0) > (best.samples?.lat?.length || 0)) best = lap;
    }
    const s = best.samples;
    if (!s?.lat || !s?.lon || s.lat.length < 5) return null;
    return s;
  }, [telemetry]);

  // GPS dot — uses trackPath (full circuit) but the cursor index comes from
  // the currently-selected lap's time so the dot stays in sync with the video.
  // Channel chart data (time-axis); we sample every Nth point so recharts
  // doesn't choke on 6000+ points.
  const chartData = useMemo(() => {
    if (!samples?.time_s) return [];
    const n = samples.time_s.length;
    const step = Math.max(1, Math.floor(n / 600));
    const rows = [];
    for (let i = 0; i < n; i += step) {
      const row = { t: samples.time_s[i] };
      for (const c of ALL_CHANNELS) {
        if (Array.isArray(samples[c.key])) row[c.key] = samples[c.key][i];
      }
      rows.push(row);
    }
    return rows;
  }, [samples]);

  // -- Render -------------------------------------------------------------- //
  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center text-white">
        <Loader2 className="w-6 h-6 animate-spin mr-2" /> Cargando…
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      <header className="sticky top-0 z-20 bg-neutral-950/90 backdrop-blur border-b border-neutral-800">
        <div className="max-w-7xl mx-auto p-3 flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(mode === 'session' ? '/telemetry' : '/pit-engineer')}
            data-testid="replay-back"
            className="text-gray-400 hover:text-white"
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> Volver
          </Button>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg font-bold flex items-center gap-2">
              <Video className="w-5 h-5 text-red-500" />
              Replay Studio
              <span className="text-xs text-gray-500 font-normal truncate">
                {mode === 'session'
                  ? `· ${event?.pilot || ''} · ${event?.circuit || ''} · ${event?.name || ''}`
                  : `· ${event?.pilot || ''} · ${event?.circuit || ''} · ${tandaKey || ''}`}
              </span>
            </h1>
            {replay && (
              <div className="mt-1">
                <ReplayStatusBadge status={replay.file_status} sourceType={replay.source_type} />
              </div>
            )}
          </div>
          {replay && (
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={runDiagnostics}
                data-testid="replay-diagnostics-btn"
                title="Correr diagnóstico de streaming"
                className="text-gray-500 hover:text-amber-400 text-[11px] uppercase tracking-wider"
              >
                Diag
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={deleteReplay}
                data-testid="replay-delete"
                className="text-gray-500 hover:text-red-400"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-4 space-y-4">
        <BetaStorageDisclaimer />
        {health && health.status === 'FAIL' && (
          <div
            data-testid="replay-health-alert"
            className="border-2 border-red-700 bg-red-950/60 rounded-md p-3 text-sm flex flex-col sm:flex-row sm:items-start gap-2"
          >
            <div className="text-red-400 font-mono text-xs uppercase tracking-wider shrink-0">
              ⚠ Runtime degradado
            </div>
            <div className="flex-1 space-y-1">
              <div className="text-red-200 text-[13px]">
                {health.error || 'Algún chequeo de salud del Replay Studio falló.'}
              </div>
              <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] font-mono text-red-300/80">
                <span data-testid="health-flag-ffmpeg">
                  ffmpeg: {health.ffmpeg_available ? '✓' : '✗'}
                </span>
                <span data-testid="health-flag-ffprobe">
                  ffprobe: {health.ffprobe_available ? '✓' : '✗'}
                </span>
                <span data-testid="health-flag-write">
                  storage_write: {health.storage_write ? '✓' : '✗'}
                </span>
                <span data-testid="health-flag-read">
                  storage_read: {health.storage_read ? '✓' : '✗'}
                </span>
                <span data-testid="health-flag-delete">
                  storage_delete: {health.storage_delete ? '✓' : '✗'}
                </span>
                {health.storage_backend === 'object' && (
                  <>
                    <span data-testid="health-flag-object-write">
                      object_store_write: {health.object_store_write ? '✓' : '✗'}
                    </span>
                    <span data-testid="health-flag-object-read">
                      object_store_read: {health.object_store_read ? '✓' : '✗'}
                    </span>
                  </>
                )}
              </div>
              <div className="text-[11px] text-red-300/70">
                Subir videos con codec HEVC o con <code>moov-at-end</code> (iPhone/GoPro) puede fallar.
                Avisá a soporte y/o re-deployá con ffmpeg incluido en la imagen.
              </div>
            </div>
          </div>
        )}
        {/* No replay yet — show upload + external URL options. */}
        {!replay && (
          <Card className="bg-neutral-900 border-neutral-800" data-testid="replay-empty">
            <CardHeader>
              <CardTitle className="text-base">Asociar un video onboard a esta tanda</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Button
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                  data-testid="replay-upload-btn"
                  className="bg-red-600 hover:bg-red-700 text-white h-10"
                >
                  {uploading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
                  {uploading ? `Subiendo ${uploadPct}%` : 'Subir mp4/mov'}
                </Button>
                <input
                  ref={fileRef}
                  type="file"
                  accept="video/mp4,video/quicktime,.mp4,.mov,.m4v"
                  className="hidden"
                  onChange={(e) => uploadFile(e.target.files?.[0])}
                  data-testid="replay-file-input"
                />
                <p className="text-[11px] text-gray-500 mt-1">
                  Hasta 3 GB. En upload local los archivos pueden borrarse si re-deployás la app — para archivos críticos preferí URL externa.
                </p>
                <p className="text-[11px] text-blue-300/80 mt-1">
                  💡 Si exportás desde VLC, elegí <strong>Video - H.264 + MP3 (MP4)</strong>. Si subís HEVC (iPhone) lo convertimos solos en el servidor.
                </p>
              </div>
              <div>
                <button
                  type="button"
                  onClick={() => setShowExternal((v) => !v)}
                  data-testid="replay-external-toggle"
                  className="text-sm text-blue-400 hover:underline flex items-center gap-1"
                >
                  <Link2 className="w-3.5 h-3.5" /> …o pegar una URL externa (Google Drive, Dropbox, .mp4 directo)
                </button>
                {showExternal && (
                  <div className="mt-2 flex items-center gap-2 flex-wrap" data-testid="replay-external-row">
                    <Input
                      value={externalUrl}
                      onChange={(e) => setExternalUrl(e.target.value)}
                      placeholder="https://drive.google.com/uc?export=download&id=..."
                      data-testid="replay-external-input"
                      className="flex-1 min-w-[280px] h-9 bg-neutral-800 border-neutral-700 text-white"
                    />
                    <Button onClick={saveExternal} className="h-9 bg-blue-600 hover:bg-blue-700" data-testid="replay-external-save">
                      Guardar URL
                    </Button>
                  </div>
                )}
              </div>
              {!telemetry && (
                <div className="text-xs text-yellow-300 border border-yellow-800/40 bg-yellow-950/30 rounded px-2 py-1.5">
                  ⚠ Esta tanda no tiene telemetría DAQ vinculada. Podés subir el video igual, pero los overlays y gráficos no van a aparecer hasta que enlaces una sesión DAQ.
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {replay && (
          <>
            {/* Transcoding banner */}
            {replay.transcoding_status === 'in_progress' && (
              <div className="bg-blue-950/50 border border-blue-700/50 text-blue-200 rounded p-3 text-sm flex items-center gap-2" data-testid="replay-transcoding-banner">
                <Loader2 className="w-4 h-4 animate-spin" />
                <div className="flex-1">
                  <strong>Convirtiendo el video a H.264…</strong> Tu archivo está en {replay.video_codec?.toUpperCase() || 'un códec no compatible'} y los navegadores de escritorio no lo reproducen. Esto puede tardar 1–5 minutos según el tamaño. La página se actualiza sola cuando termine.
                </div>
              </div>
            )}
            {replay.transcoding_status === 'failed' && (
              <div className="bg-red-950/50 border border-red-700/50 text-red-200 rounded p-3 text-sm flex items-start gap-2" data-testid="replay-transcoding-failed">
                <div className="flex-1">
                  <strong>No se pudo convertir el video.</strong> Codec original: <code className="text-red-300">{replay.video_codec || '?'}</code>. Re-exportalo desde VLC con el perfil <em>"Video - H.264 + MP3 (MP4)"</em> y volvé a subirlo, o tocá "Reintentar".
                </div>
                <Button size="sm" onClick={async () => {
                  try {
                    const res = await axios.post(`${API}/replay/${replay.id}/transcode`, {}, { withCredentials: true });
                    setReplay(res.data.replay);
                    toast.success(res.data.scheduled ? 'Conversión reiniciada' : 'No requiere conversión');
                  } catch (e) { toast.error('No se pudo reintentar'); }
                }} className="h-7 bg-red-700 hover:bg-red-600" data-testid="replay-transcode-retry">
                  Reintentar
                </Button>
              </div>
            )}

            {/* Hidden file input for re-upload when video is missing. */}
            <input
              ref={fileRef}
              type="file"
              accept="video/mp4,video/quicktime,.mp4,.mov,.m4v"
              className="hidden"
              onChange={(e) => uploadFile(e.target.files?.[0])}
              data-testid="replay-reupload-file-input"
            />

            {/* Video + GPS row */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-3">
              <Card className="bg-black border-neutral-800 lg:col-span-9 overflow-hidden">
                <div ref={videoBoxRef} className="relative">
                  {replay.file_status === 'missing' ? (
                    <ReplayMissingPlaceholder
                      replay={replay}
                      onReupload={() => fileRef.current?.click()}
                    />
                  ) : (
                  <video
                    ref={videoRef}
                    src={(() => {
                      if (!replay) return undefined;
                      // Cache-bust on transcode completion so the player
                      // re-fetches the newly-remuxed file.
                      const cb = videoVersion > 0 ? `cb=${videoVersion}` : '';
                      if (replay.source_type !== 'local') {
                        if (!cb) return replay.video_url;
                        const sep = replay.video_url.includes('?') ? '&' : '?';
                        return `${replay.video_url}${sep}${cb}`;
                      }
                      const tok = localStorage.getItem('datapro_session_token');
                      const params = [];
                      if (tok) params.push(`token=${encodeURIComponent(tok)}`);
                      if (cb) params.push(cb);
                      const q = params.length ? `?${params.join('&')}` : '';
                      return `${BACKEND_URL}${replay.video_url}${q}`;
                    })()}
                    controls
                    preload="metadata"
                    playsInline
                    onLoadedMetadata={(e) => {
                      const d = e.target.duration;
                      if (d && Number.isFinite(d) && !replay.video_duration) {
                        axios.patch(`${API}/replay/${replay.id}`, { video_duration: d }, { withCredentials: true }).catch(() => {});
                      }
                    }}
                    onPlay={() => setIsPlaying(true)}
                    onPause={() => setIsPlaying(false)}
                    onError={(e) => {
                      const code = e?.target?.error?.code;
                      const src = e?.target?.currentSrc || e?.target?.src;
                      // First try a tiny GET so we get the structured JSON
                      // error from the backend (HEAD doesn't carry body
                      // through some proxies).
                      const probeBackend = () => {
                        if (!src || !src.includes('/api/replay/file/')) return Promise.resolve(null);
                        return fetch(src.replace(/&cb=\d+/, '') + '&_probe=1', {
                          headers: { Range: 'bytes=0-0' },
                          credentials: 'include',
                        }).then(async (r) => {
                          if (r.ok) return null;  // streaming itself is fine → it's a codec/decoder issue
                          let body = null;
                          try { body = await r.json(); } catch (_) {}
                          return { status: r.status, err: body?.detail?.error || body?.error, body };
                        }).catch(() => null);
                      };
                      // Codec / "no se pudo reproducir" actionable messages.
                      const ERR_MESSAGES = {
                        'file_not_found': 'El archivo no existe en el servidor. Si re-deployaste hace poco se perdió el almacenamiento local — volvé a subir el video.',
                        'expired_session': 'Tu sesión expiró. Cerrá sesión y volvé a entrar.',
                        'unknown_token': 'Sesión inválida. Volvé a iniciar sesión.',
                        'missing_token': 'No se pudo autenticar la petición de video.',
                        'invalid_filename': 'Nombre de archivo inválido.',
                        'user_not_found': 'El usuario asociado ya no existe.',
                      };
                      probeBackend().then((probe) => {
                        if (probe && probe.err) {
                          const msg = ERR_MESSAGES[probe.err]
                            || `Servidor respondió ${probe.status}: ${probe.body?.detail?.detail || 'sin detalles'}`;
                          toast.error(msg, {
                            duration: 10000,
                            action: { label: 'Diagnóstico', onClick: () => runDiagnostics() },
                          });
                          return;
                        }
                        // Backend served bytes OK → it's a codec/decoder error.
                        if (code === 4 && replay?.source_type === 'local'
                            && replay?.transcoding_status !== 'in_progress') {
                          toast.message('Optimizando el video para reproducción…');
                          axios.post(`${API}/replay/${replay.id}/transcode`, {}, { withCredentials: true })
                            .then((res) => {
                              if (res.data?.scheduled === false) {
                                toast.error(
                                  'Este servidor no puede convertir el video. Exportalo desde HandBrake o VLC con perfil "Web Optimised" / "H.264 + Faststart" y subilo de nuevo.',
                                  { duration: 10000 },
                                );
                              } else {
                                setReplay(res.data.replay);
                              }
                            })
                            .catch((err) => toast.error(err?.response?.data?.detail || 'No se pudo iniciar la conversión'));
                          return;
                        }
                        toast.error(`No se pudo reproducir el video (código ${code ?? '?'}). Revisá el formato (mp4/mov) o reasociá el archivo.`, {
                          duration: 10000,
                          action: { label: 'Diagnóstico', onClick: () => runDiagnostics() },
                        });
                      });
                    }}
                    data-testid="replay-video"
                    className="w-full aspect-video bg-black"
                  />
                  )}

                  {/* Dynamic overlay widgets (KPI / sparkline / map / logo) */}
                  {showOverlayCharts && overlayLayout.map((w) => {
                    const ch = w.channel ? ALL_CHANNELS.find((c) => c.key === w.channel) : null;
                    let body = null;
                    if (w.type === 'logo') {
                      body = <OverlayLogo />;
                    } else if (samples && w.type === 'lap_time') {
                      body = <OverlayLapTime samples={samples} color={w.color} subscribe={subscribe} getTime={getTime} />;
                    } else if (samples && w.type === 'delta') {
                      body = <OverlayDelta samples={samples} ghostSamples={ghostSamples} subscribe={subscribe} getTime={getTime} />;
                    } else if (samples && w.type === 'kpi' && ch) {
                      body = <OverlayKPI samples={samples} channel={ch} color={w.color} subscribe={subscribe} getTime={getTime} ghostSamples={ghostSamples} ghostColor={ghostColor} />;
                    } else if (samples && w.type === 'sparkline' && ch) {
                      body = <OverlaySparkline samples={samples} channel={ch} color={w.color} subscribe={subscribe} getTime={getTime} ghostSamples={ghostSamples} ghostColor={ghostColor} />;
                    } else if (samples && w.type === 'map') {
                      body = <TrackMapOverlay samples={samples} color={w.color} subscribe={subscribe} getTime={getTime} />;
                    }
                    if (!body) return null;
                    return (
                      <OverlayWidget
                        key={w.id}
                        widget={w}
                        containerRef={videoBoxRef}
                        editing={editLayout}
                        locked={w.type === 'logo'}
                        label={w.type === 'logo' ? 'DATAPRO (logo)' : (w.type === 'lap_time' ? 'Lap Time' : (w.type === 'delta' ? 'Δ vs Ghost' : (ch?.label || (w.type === 'map' ? 'Mapa' : w.type))))}
                        onChange={(p) => updateWidget(w.id, p)}
                        onRemove={() => removeWidget(w.id)}
                      >
                        {body}
                      </OverlayWidget>
                    );
                  })}

                  {/* Floating toolbar */}
                  {samples && (
                    <div className="absolute bottom-12 right-3 z-10 flex flex-col gap-1.5 items-end">
                      <button
                        type="button"
                        onClick={() => setShowOverlayCharts((v) => !v)}
                        data-testid="overlay-charts-toggle"
                        title={showOverlayCharts ? 'Ocultar overlays' : 'Mostrar overlays'}
                        className="bg-black/70 hover:bg-black/90 border border-white/15 text-white rounded h-8 w-8 flex items-center justify-center"
                      >
                        {showOverlayCharts ? <EyeOff className="w-4 h-4" /> : <LineChartIcon className="w-4 h-4" />}
                      </button>
                      <button
                        type="button"
                        onClick={() => setEditLayout((v) => !v)}
                        data-testid="overlay-edit-toggle"
                        title={editLayout ? 'Salir de edición' : 'Editar layout'}
                        className={`${editLayout ? 'bg-purple-600 hover:bg-purple-500' : 'bg-black/70 hover:bg-black/90'} border border-white/15 text-white rounded h-8 w-8 flex items-center justify-center`}
                      >
                        <Settings2 className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>

                {/* Inline sync bar — sits right under the player so the
                    user adjusts offset while looking at the same frame.
                    Replicates the most-used controls of the advanced
                    sidebar panel (which stays for power-user actions). */}
                {replay && replay.file_status !== 'missing' && (
                  <div
                    className="border-t border-neutral-800 bg-neutral-950 px-3 py-2"
                    data-testid="inline-sync-bar"
                  >
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-[10px] uppercase tracking-wider text-gray-500 shrink-0">Offset</span>

                      {/* Big numeric readout — always visible, monospaced */}
                      <span
                        className="font-mono text-sm tabular-nums text-white shrink-0 min-w-[78px] text-right"
                        data-testid="inline-offset-value"
                        title={`${offset.toFixed(3)} s`}
                      >
                        {offset >= 0 ? '+' : ''}{(offset * 1000).toFixed(0)} ms
                      </span>

                      {/* Slider — wide, drag-driven, debounced via the
                          setOffset PATCH which is itself optimistic. */}
                      <input
                        type="range"
                        min={-30}
                        max={30}
                        step={0.01}
                        value={offset}
                        onChange={(e) => setOffset(parseFloat(e.target.value))}
                        data-testid="inline-offset-slider"
                        className="flex-1 min-w-[140px] accent-red-600 h-1"
                        title="Arrastrá para ajustar el offset. Los overlays se actualizan al instante."
                      />

                      {/* Fine-step buttons — same testids as the advanced
                          panel so existing tests / hotkeys still work. */}
                      <div className="flex items-center gap-0.5 shrink-0">
                        <FineBtn onClick={() => setOffset(offset - 1)}    testid="inline-offset-minus-1s"    title="−1 s">−1s</FineBtn>
                        <FineBtn onClick={() => setOffset(offset - 0.1)} testid="inline-offset-minus-100ms" title="−100 ms (Shift + ←)">−100</FineBtn>
                        <FineBtn onClick={() => setOffset(offset - 0.05)} testid="inline-offset-minus-50ms"  title="−50 ms">−50</FineBtn>
                        <FineBtn onClick={() => setOffset(offset - 0.01)} testid="inline-offset-minus-10ms"  title="−10 ms (Alt + ←)">−10</FineBtn>
                        <FineBtn onClick={() => setOffset(offset + 0.01)} testid="inline-offset-plus-10ms"   title="+10 ms (Alt + →)">+10</FineBtn>
                        <FineBtn onClick={() => setOffset(offset + 0.05)} testid="inline-offset-plus-50ms"   title="+50 ms">+50</FineBtn>
                        <FineBtn onClick={() => setOffset(offset + 0.1)}  testid="inline-offset-plus-100ms"  title="+100 ms (Shift + →)">+100</FineBtn>
                        <FineBtn onClick={() => setOffset(offset + 1)}    testid="inline-offset-plus-1s"     title="+1 s">+1s</FineBtn>
                      </div>

                      {/* Frame stepping — these depend on rVFC, work in pause too. */}
                      <div className="flex items-center gap-0.5 shrink-0 ml-1">
                        <FineBtn
                          onClick={() => {
                            const v = videoRef.current;
                            if (!v) return;
                            v.pause();
                            v.currentTime = Math.max(0, v.currentTime - (frameDurationRef.current || 1 / 30));
                          }}
                          testid="inline-frame-prev"
                          title="Frame anterior (←)"
                        >◀ frame</FineBtn>
                        <FineBtn
                          onClick={() => {
                            const v = videoRef.current;
                            if (!v) return;
                            v.pause();
                            v.currentTime = Math.min(v.duration || 0, v.currentTime + (frameDurationRef.current || 1 / 30));
                          }}
                          testid="inline-frame-next"
                          title="Frame siguiente (→)"
                        >frame ▶</FineBtn>
                      </div>

                      {/* "Sync acá" — sets offset so the current video
                          frame aligns with the start of the selected lap.
                          The same operation lives in the sidebar with a
                          fuller label; here we keep the icon-only variant. */}
                      <button
                        type="button"
                        onClick={() => setOffset(-(videoRef.current?.currentTime || 0))}
                        data-testid="inline-offset-sync-here"
                        className="shrink-0 h-7 px-2 rounded bg-purple-700 hover:bg-purple-600 text-white text-[11px] inline-flex items-center gap-1"
                        title="Pausá en el frame donde arranca la vuelta y apretá acá"
                      >
                        <Crosshair className="w-3 h-3" />
                        Sync acá
                      </button>

                      {/* Reset offset to 0 — convenient when experimenting. */}
                      <button
                        type="button"
                        onClick={() => setOffset(0)}
                        data-testid="inline-offset-reset"
                        className="shrink-0 h-7 px-2 rounded bg-neutral-800 hover:bg-neutral-700 text-gray-300 text-[11px]"
                        title="Volver el offset a 0"
                        disabled={offset === 0}
                      >
                        0
                      </button>
                    </div>
                  </div>
                )}

                {/* Timeline with annotation markers — clickable to seek. */}
                {telemetry?.annotations?.length > 0 && samples && (() => {
                  // Map each annotation to a fraction of the lap.
                  const lapDuration = samples.time_s?.[samples.time_s.length - 1] || 0;
                  if (lapDuration <= 0) return null;
                  const lapAnns = telemetry.annotations.filter((a) =>
                    a.time_s != null && (a.lap_number == null || a.lap_number === selectedLap),
                  );
                  if (lapAnns.length === 0) return null;
                  return (
                    <div
                      className="relative h-5 bg-neutral-900 border-t border-neutral-800"
                      data-testid="annotation-timeline"
                      title="Marcas de anotaciones"
                    >
                      {lapAnns.map((a) => {
                        const pct = Math.max(0, Math.min(1, a.time_s / lapDuration)) * 100;
                        const iconDef = ANNOTATION_ICONS.find((i) => i.key === a.icon) || ANNOTATION_ICONS[ANNOTATION_ICONS.length - 1];
                        return (
                          <button
                            key={a.id}
                            type="button"
                            onClick={() => {
                              const v = videoRef.current;
                              if (v) v.currentTime = Math.max(0, a.time_s - offset);
                            }}
                            data-testid={`timeline-marker-${a.id}`}
                            title={`${a.text}\n${a.time_s.toFixed(3)}s`}
                            className="absolute -top-0.5 -translate-x-1/2 text-xs leading-none hover:scale-125 transition-transform"
                            style={{ left: `${pct}%`, color: a.color || iconDef.color }}
                          >
                            {iconDef.emoji}
                          </button>
                        );
                      })}
                    </div>
                  );
                })()}

                {/* Layout edit panel — slides in below the video when active */}
                {editLayout && samples && (
                  <div className="border-t border-neutral-800 p-3 space-y-2 bg-neutral-950" data-testid="overlay-edit-panel">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div className="text-xs text-gray-400">
                        Arrastrá los widgets sobre el video. Esquina blanca = redimensionar. Cambios se guardan automáticamente.
                      </div>
                      <Button size="sm" variant="ghost" onClick={resetLayout} className="h-7 text-gray-300 hover:text-white" data-testid="overlay-reset-layout">
                        <RotateCcw className="w-3 h-3 mr-1" /> Reset layout
                      </Button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-2">
                      <div>
                        <Label className="text-[10px] text-gray-400">Agregar KPI</Label>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {ALL_CHANNELS.filter((c) => Array.isArray(samples?.[c.key])).map((c) => (
                            <button key={`add-kpi-${c.key}`} type="button" onClick={() => addWidget('kpi', c.key)}
                              data-testid={`add-kpi-${c.key}`}
                              className="text-[11px] px-2 py-1 rounded bg-neutral-800 hover:bg-neutral-700 text-white border border-neutral-700">
                              <Plus className="w-3 h-3 inline mr-1" style={{ color: c.color }} />{c.label}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div>
                        <Label className="text-[10px] text-gray-400">Agregar gráfico</Label>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {ALL_CHANNELS.filter((c) => Array.isArray(samples?.[c.key])).map((c) => (
                            <button key={`add-spark-${c.key}`} type="button" onClick={() => addWidget('sparkline', c.key)}
                              data-testid={`add-spark-${c.key}`}
                              className="text-[11px] px-2 py-1 rounded bg-neutral-800 hover:bg-neutral-700 text-white border border-neutral-700">
                              <Plus className="w-3 h-3 inline mr-1" style={{ color: c.color }} />{c.label}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div>
                        <Label className="text-[10px] text-gray-400">Mapa & Lap time</Label>
                        <div className="flex flex-wrap gap-1 mt-1">
                          <button type="button" onClick={() => addWidget('map')} data-testid="add-map"
                            className="text-[11px] px-2 py-1 rounded bg-neutral-800 hover:bg-neutral-700 text-white border border-neutral-700">
                            <Plus className="w-3 h-3 inline mr-1" />Agregar track map
                          </button>
                          <button type="button" onClick={() => addWidget('lap_time')} data-testid="add-laptime"
                            className="text-[11px] px-2 py-1 rounded bg-neutral-800 hover:bg-neutral-700 text-white border border-neutral-700">
                            <Plus className="w-3 h-3 inline mr-1" style={{ color: '#fde047' }} />Agregar Lap Time
                          </button>
                          <button type="button" onClick={() => addWidget('delta')} data-testid="add-delta"
                            className="text-[11px] px-2 py-1 rounded bg-neutral-800 hover:bg-neutral-700 text-white border border-neutral-700">
                            <Plus className="w-3 h-3 inline mr-1" style={{ color: '#22c55e' }} />Agregar Δ vs Ghost
                          </button>
                        </div>
                      </div>
                    </div>
                    {/* Per-widget controls */}
                    <div className="pt-3 border-t border-neutral-800 max-h-56 overflow-y-auto">
                      <table className="w-full text-[11px]">
                        <thead className="text-gray-500">
                          <tr>
                            <th className="text-left pb-1">Widget</th>
                            <th className="text-left pb-1">Color</th>
                            <th className="text-left pb-1">Visible</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {overlayLayout.map((w) => {
                            const ch = w.channel ? ALL_CHANNELS.find((c) => c.key === w.channel) : null;
                            const isLogo = w.type === 'logo';
                            return (
                              <tr key={`row-${w.id}`} className="border-t border-neutral-900" data-testid={`overlay-row-${w.id}`}>
                                <td className="py-1 text-white">
                                  <span className="opacity-60 mr-1">{w.type}</span>
                                  {isLogo ? 'DATAPRO 🔒' : (w.type === 'lap_time' ? 'Lap Time' : (w.type === 'delta' ? 'Δ vs Ghost' : (ch?.label || (w.type === 'map' ? 'Mapa' : ''))))}
                                </td>
                                <td className="py-1">
                                  {!isLogo && (
                                    <input
                                      type="color"
                                      value={w.color || '#60a5fa'}
                                      onChange={(e) => updateWidget(w.id, { color: e.target.value })}
                                      data-testid={`overlay-color-${w.id}`}
                                      className="w-7 h-5 bg-transparent border border-neutral-700 rounded cursor-pointer"
                                    />
                                  )}
                                </td>
                                <td className="py-1">
                                  {!isLogo && (
                                    <input
                                      type="checkbox"
                                      checked={w.visible !== false}
                                      onChange={(e) => updateWidget(w.id, { visible: e.target.checked })}
                                      data-testid={`overlay-visible-${w.id}`}
                                    />
                                  )}
                                </td>
                                <td className="py-1 text-right">
                                  {!isLogo && (
                                    <button type="button" onClick={() => removeWidget(w.id)}
                                      data-testid={`overlay-remove-${w.id}`}
                                      className="text-red-400 hover:text-red-300">
                                      <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                          {overlayLayout.length === 0 && (
                            <tr><td colSpan={4} className="text-center text-gray-500 py-3">Sin widgets. Agregá uno arriba.</td></tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </Card>

              <Card className="bg-neutral-900 border-neutral-800 lg:col-span-3">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-red-500" /> Trazado GPS
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <SidebarGPS
                    trackPath={trackPath}
                    samples={samples}
                    subscribe={subscribe}
                    getTime={getTime}
                    isVbo={telemetry?.format === 'vbo'}
                  />
                  {/* Annotations: time-bound coaching notes synced to the video. */}
                  {telemetry?.session_id && (
                    <div className="mt-3 border-t border-neutral-800 pt-3">
                      <AnnotationsPanel
                        sessionId={telemetry.session_id}
                        lapNumber={selectedLap}
                        getTime={getTime}
                        onSeek={(t) => {
                          const v = videoRef.current;
                          if (!v) return;
                          v.currentTime = Math.max(0, (t ?? 0) - offset);
                        }}
                        telemetry={telemetry}
                        onChange={(updater) => setTelemetry(updater)}
                      />
                    </div>
                  )}
                  {/* Sync + lap controls. The offset slider, fine-step
                      buttons, frame stepping and "Sync acá" used to live
                      here too; they moved into the inline bar right below
                      the player to keep a single source of truth. What
                      stays in the sidebar is scope-related (which lap to
                      sync against) and playback rate. */}
                  <div className="mt-3 space-y-2">
                    {telemetry && telemetry.laps?.length > 1 && (
                      <div>
                        <Label className="text-[10px] text-gray-400">Vuelta a sincronizar</Label>
                        <select
                          value={selectedLap || ''}
                          onChange={(e) => setLapNumber(Number(e.target.value))}
                          data-testid="replay-lap-select"
                          className="w-full h-8 mt-0.5 bg-neutral-800 border border-neutral-700 text-white text-xs rounded px-2"
                        >
                          {telemetry.laps.map((l) => (
                            <option key={l.lap_number} value={l.lap_number}>
                              Lap {l.lap_number} ({l.lap_time_s.toFixed(3)}s)
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                    <div>
                      <Label className="text-[10px] text-gray-400">Velocidad de reproducción</Label>
                      <div className="grid grid-cols-6 gap-1 mt-0.5">
                        {[0.1, 0.25, 0.5, 1, 1.5, 2].map((r) => (
                          <button
                            key={r}
                            type="button"
                            onClick={() => setPlaybackRate(r)}
                            data-testid={`playback-rate-${r}`}
                            className={`h-7 rounded text-[10px] font-mono ${
                              Math.abs(playbackRate - r) < 1e-6
                                ? 'bg-red-700 text-white'
                                : 'bg-neutral-800 text-gray-300 hover:bg-neutral-700'
                            }`}
                            title={`Reproducir a ${r}x`}
                          >
                            {r}x
                          </button>
                        ))}
                      </div>
                    </div>
                    {/* Ghost comparison — overlay a SECOND telemetry session
                        on top of the main one. Lines up by circuit distance,
                        not stopwatch time, so corners match even if the lap
                        times differ. */}
                    {telemetry && (
                      <div className="border-t border-neutral-800 pt-2 mt-2 space-y-1.5" data-testid="ghost-panel">
                        <div className="flex items-center justify-between">
                          <Label className="text-[10px] text-gray-400">Comparar con (Ghost)</Label>
                          <label className="flex items-center gap-1 text-[10px] text-gray-400 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={ghostEnabled}
                              onChange={(e) => setGhostEnabled(e.target.checked)}
                              data-testid="ghost-toggle"
                              className="cursor-pointer"
                            />
                            on
                          </label>
                        </div>
                        <select
                          value={ghostSessionId}
                          onChange={(e) => setGhostSessionId(e.target.value)}
                          data-testid="ghost-session-select"
                          className="w-full h-8 bg-neutral-800 border border-neutral-700 text-white text-xs rounded px-2"
                        >
                          <option value="">— Sin ghost —</option>
                          {ghostSessions.map((s) => (
                            <option key={s.session_id} value={s.session_id}>
                              {(s.name || s.filename || s.session_id).slice(0, 40)}
                              {s.fastest_lap_s ? ` · ${s.fastest_lap_s.toFixed(3)}s` : ''}
                            </option>
                          ))}
                        </select>
                        {ghostTelemetry && ghostTelemetry.laps?.length > 0 && (
                          <select
                            value={ghostLapNumber || ''}
                            onChange={(e) => setGhostLapNumber(Number(e.target.value))}
                            data-testid="ghost-lap-select"
                            className="w-full h-8 bg-neutral-800 border border-neutral-700 text-white text-xs rounded px-2"
                          >
                            {ghostTelemetry.laps.map((l) => (
                              <option key={l.lap_number} value={l.lap_number}>
                                Lap {l.lap_number} ({l.lap_time_s.toFixed(3)}s)
                              </option>
                            ))}
                          </select>
                        )}
                        {ghostTelemetry && (
                          <div className="flex items-center gap-2">
                            <Label className="text-[10px] text-gray-400 flex-1">Color ghost</Label>
                            <input
                              type="color"
                              value={ghostColor}
                              onChange={(e) => setGhostColor(e.target.value)}
                              data-testid="ghost-color"
                              className="w-8 h-6 bg-transparent border border-neutral-700 rounded cursor-pointer"
                            />
                          </div>
                        )}
                        {ghostCircuitMismatch && (
                          <div className="text-[10px] text-amber-400 bg-amber-950/30 border border-amber-800/40 rounded px-2 py-1" data-testid="ghost-circuit-warning">
                            ⚠ Distintos circuitos: <span className="text-gray-300">{telemetry.circuit || '?'}</span> vs <span className="text-gray-300">{ghostTelemetry?.circuit || '?'}</span>. La comparación por distancia no es válida.
                          </div>
                        )}
                        {ghostSamples && (
                          <div className="text-[9px] text-gray-500 italic">
                            Línea punteada {ghostColor} en los gráficos. Δ vs Ghost agregable en el panel de edición de overlays.
                          </div>
                        )}
                      </div>
                    )}
                    <div className="text-[9px] text-gray-500 leading-tight">
                      ⌨ Atajos:&nbsp;
                      <span className="text-gray-300">Espacio</span> play/pausa ·&nbsp;
                      <span className="text-gray-300">←→</span> frame ·&nbsp;
                      <span className="text-gray-300">Shift+←→</span> ±100ms ·&nbsp;
                      <span className="text-gray-300">Alt+←→</span> ±10ms ·&nbsp;
                      <span className="text-gray-300">Shift+Espacio</span> slow-mo
                    </div>
                    <div className="text-[10px] text-gray-500 italic leading-relaxed border-t border-neutral-800 pt-2">
                      Para sincronizar usá la barra debajo del video.
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Channel charts */}
            {samples && (
              <Card className="bg-neutral-900 border-neutral-800" data-testid="replay-channels">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Canales sincronizados</CardTitle>
                </CardHeader>
                <CardContent className="pt-0 space-y-2">
                  {ALL_CHANNELS.filter((c) => Array.isArray(samples?.[c.key])).map((c) => (
                    <div key={c.key} data-testid={`replay-chart-${c.key}`}>
                      <div className="text-[11px] text-gray-400 mb-1 flex items-center gap-2">
                        <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: c.color }} />
                        {c.label} <span className="text-gray-600">({c.unit})</span>
                        <LiveValueLabel
                          samples={samples}
                          channel={c}
                          subscribe={subscribe}
                          getTime={getTime}
                          decimals={c.key === 'rpm' ? 0 : 2}
                        />
                      </div>
                      <div className="h-24">
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart
                            data={chartData}
                            onClick={(p) => {
                              // Bidirectional sync: click the chart to seek
                              // the video. The chart's x-axis is lap-relative
                              // time; convert to absolute video time using the
                              // current offset.
                              const tLap = p?.activeLabel;
                              const v = videoRef.current;
                              if (tLap != null && v) {
                                const newVideo = Math.max(0, tLap - offset);
                                v.currentTime = newVideo;
                              }
                            }}
                          >
                            <CartesianGrid strokeDasharray="3 3" stroke="#1f1f1f" />
                            <XAxis dataKey="t" type="number" domain={['dataMin', 'dataMax']} stroke="#666" fontSize={10} tickFormatter={(v) => `${v.toFixed(1)}s`} />
                            <YAxis stroke="#666" fontSize={10} width={42} />
                            <Tooltip contentStyle={{ background: '#0a0a0a', border: '1px solid #333', fontSize: 11 }} />
                            <Line type="monotone" dataKey={c.key} stroke={c.color} strokeWidth={1.5} dot={false} isAnimationActive={false} />
                            <ReferenceLine x={telemetryTime} stroke="#9ca3af" strokeDasharray="2 2" isFront />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}
          </>
        )}
      </main>
    </div>
  );
}
